import { useRequiredContext } from "@redotech/react-util/context";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ReloadOutboundViewsContext } from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { ReloadParcelsContext } from "@redotech/redo-merchant-fulfillment-app/services/parcel-service";
import { Message } from "@redotech/redo-model/agents/llm-messages";
import {
  AiChatProvider,
  AiChatStreamEvent,
} from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import {
  extractAttachmentUrls,
  extractTextFromContent,
  transformMessagesForBackend,
} from "@redotech/redo-web/ai/chat/utils/messages";
import { ReloadTeamContext } from "@redotech/redo-web/context/team-context";
import { UploaderContext } from "@redotech/redo-web/image-upload";
import * as React from "react";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EscalateToHumanTool } from "../../help-chat/escalate-to-human-tool";
import {
  EscalationProvider,
  useEscalationContext,
} from "../../help-chat/escalation-context";
import { HelpChatGlobal } from "../../help-chat/help-chat-global";
import { useInChatEscalationFlag } from "../../help-chat/in-chat-escalation-flag-provider";
import { QueryReturnsTool } from "./global-search-tools/query-returns/query-returns-tool";
import { UpdateIntegrationTool } from "./global-search-tools/update-integration-tool";

interface GlobalAIProps {
  children?: React.ReactNode;
}

function isOmsSettingsPage(): boolean {
  const omsSettingsPaths: string[] = [
    "/settings/oms",
    "/outbound-labels/packing-slips",
  ];
  return omsSettingsPaths.some((path) =>
    window.location.pathname.includes(path),
  );
}

export const GlobalAI = memo(function GlobalAI({ children }: GlobalAIProps) {
  const inChatEscalation = useInChatEscalationFlag();

  // Always wrap in EscalationProvider (needed for HelpChatGlobal hook)
  // Only wrap in ClientUploader when escalation feature is enabled
  if (inChatEscalation) {
    return (
      <EscalationProvider>
        <ClientUploader>
          <GlobalAIContentWithEscalation>
            {children}
          </GlobalAIContentWithEscalation>
        </ClientUploader>
      </EscalationProvider>
    );
  }

  // Legacy behavior: wrap in EscalationProvider but don't use escalation features
  return (
    <EscalationProvider>
      <ClientUploader>
        <GlobalAIContentLegacy>{children}</GlobalAIContentLegacy>
      </ClientUploader>
    </EscalationProvider>
  );
});

/**
 * Legacy content component - matches main branch behavior (Intercom popup)
 */
const GlobalAIContentLegacy = memo(function GlobalAIContentLegacy({
  children,
}: GlobalAIProps) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const user = useRequiredContext(UserContext);
  const uploader = useContext(UploaderContext);
  const { pageContext, onPageDataChanged } = useContext(GlobalAgentContext);
  const reloadViews = useContext(ReloadOutboundViewsContext);
  const reloadParcels = useContext(ReloadParcelsContext);
  const reloadTeam = useContext(ReloadTeamContext);

  // state is used to control the UI thread context while refs are used during sends
  const [_currentThreadId, setCurrentThreadId] = useState<string | undefined>();
  const [_triageThreadId, setTriageThreadId] = useState<string | undefined>();
  const triageThreadCallbackRef = useRef<((threadId: string) => void) | null>(
    null,
  );

  // Use refs to always have the most current values for the callback
  const currentThreadIdRef = useRef<string | undefined>();
  const triageThreadIdRef = useRef<string | undefined>();

  const toolComponents = useMemo(
    () => ({
      "update-integration": UpdateIntegrationTool,
      "query-returns": QueryReturnsTool,
      "escalate-to-human": EscalateToHumanTool,
    }),
    [],
  );

  const handleToolResult = useCallback(async (args: any) => {
    if (args.toolName === "escalate-to-human") {
      // No command palette to close in AI-only mode
    }
  }, []);

  const handleChatOpen = useCallback(() => {
    setCurrentThreadId(undefined);
    setTriageThreadId(undefined);
    currentThreadIdRef.current = undefined;
    triageThreadIdRef.current = undefined;
  }, []);

  const handleLoadThread = useCallback((threadId: string, _thread: unknown) => {
    // When loading from thread history, always continue with the triage agent
    // The triage agent will handle routing to child agents as needed
    // Continue with triage agent
    setCurrentThreadId(threadId);
    setTriageThreadId(threadId);
    currentThreadIdRef.current = threadId;
    triageThreadIdRef.current = threadId;
  }, []);

  const streamMessageToAI = useCallback(
    async function* ({
      newMessages,
      signal,
    }: {
      newMessages: Message[];
      signal: AbortSignal;
    }): AsyncGenerator<AiChatStreamEvent, void, undefined> {
      const transformedMessages = transformMessagesForBackend(newMessages);

      // Use refs to get the most current values
      const currentCurrentThreadId = currentThreadIdRef.current;
      const currentTriageThreadId = triageThreadIdRef.current;

      // Page context: if set (e.g., from Returns Ask AI), always include it
      // so the triage agent has consistent context on this page.

      const stream = merchantRpcClient.streamMerchantSupportAgent(
        {
          messages: transformedMessages,
          currentThreadId: currentCurrentThreadId,
          triageThreadId: currentTriageThreadId,
          userEmail: user.email,
          merchantTimezone: Temporal.Now.timeZoneId(),
          // Always pass pageContext when available (not only for new threads)
          ...(pageContext ? { pageContext } : {}),
        },
        { signal },
      );

      let sawOmsToolCall = false;
      for await (const event of stream) {
        switch (event.type) {
          case "text-delta":
            yield { type: "text-delta", textDelta: event.textDelta };
            break;
          case "tool-call":
            if (event.toolCall.toolName === "query-oms") {
              sawOmsToolCall = true;
            }
            yield { type: "tool-call", toolCall: event.toolCall };
            break;
          case "reasoning":
            yield { type: "reasoning", text: event.text };
            break;
          case "status":
            yield { type: "status", status: event.status };
            break;
          case "complete": {
            const { thread } = event;

            // Check if this created a new triage thread
            const wasNewTriageThread =
              !currentTriageThreadId && thread.triageThreadId;

            // Update both state and refs
            setCurrentThreadId(thread.currentThreadId.toString());
            setTriageThreadId(thread.triageThreadId.toString());
            currentThreadIdRef.current = thread.currentThreadId.toString();
            triageThreadIdRef.current = thread.triageThreadId.toString();

            // If a new triage thread was created, trigger thread refresh
            if (wasNewTriageThread) {
              triageThreadCallbackRef.current?.(
                thread.triageThreadId.toString(),
              );
            }

            // Refresh data after OMS queries (may have created/modified views or parcels)
            if (sawOmsToolCall) {
              void reloadViews?.({ onlyReloadIfChanged: true });
              void reloadParcels?.();
              onPageDataChanged?.();
              if (isOmsSettingsPage()) {
                void reloadTeam?.();
              }
            }

            yield {
              type: "complete",
              messages: thread.newMessages,
              pendingTools: thread.pendingTools,
            };
            break;
          }
          case "error":
            yield { type: "error", error: event.error };
            break;
        }
      }
    },
    [
      merchantRpcClient,
      user.email,
      pageContext,
      reloadViews,
      reloadParcels,
      reloadTeam,
      onPageDataChanged,
    ],
  );

  useEffect(() => {
    return () => {
      triageThreadCallbackRef.current = null;
    };
  }, []);

  const fileOptions = useMemo(
    () => ({
      inputAccept: "image/*",
      inputMultiple: true,
      inputLabel: "Upload from computer",
    }),
    [],
  );

  return (
    <AiChatProvider
      additionalToolComponents={toolComponents}
      fileUploadOptions={fileOptions}
      onToolResult={handleToolResult}
      streamMessageToAI={streamMessageToAI}
      uploader={uploader}
    >
      {children}
      <HelpChatGlobal
        onChatOpen={handleChatOpen}
        onLoadThread={(threadId: string, thread: unknown) =>
          handleLoadThread(threadId, thread)
        }
        onRegisterTriageThreadCallback={(
          callback: (threadId: string) => void,
        ) => {
          triageThreadCallbackRef.current = callback;
        }}
      />
    </AiChatProvider>
  );
});

/**
 * New content component with in-chat escalation support
 */
const GlobalAIContentWithEscalation = memo(
  function GlobalAIContentWithEscalation({ children }: GlobalAIProps) {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const user = useRequiredContext(UserContext);
    const uploader = useContext(UploaderContext);
    const { pageContext, onPageDataChanged } = useContext(GlobalAgentContext);
    const {
      escalation,
      setEscalation,
      setTriageThreadId: setEscalationTriageThreadId,
      clearEscalation,
    } = useEscalationContext();
    const { panelOptions, clearPanelOptions } = useAiSlidePanel();
    const reloadViews = useContext(ReloadOutboundViewsContext);
    const reloadParcels = useContext(ReloadParcelsContext);
    const reloadTeam = useContext(ReloadTeamContext);

    // Handle direct escalation from external flows (e.g., recurring pickup requests)
    useEffect(() => {
      if (!panelOptions?.directEscalation) {
        return;
      }
      const {
        escalationId,
        intercomConversationId,
        noticeTopic,
        triageThreadId,
      } = panelOptions.directEscalation;
      setEscalationTriageThreadId(triageThreadId);
      setEscalation({
        escalationId,
        intercomConversationId,
        noticeTopic,
        status: "waiting",
      });
      clearPanelOptions();
    }, [
      panelOptions,
      setEscalation,
      setEscalationTriageThreadId,
      clearPanelOptions,
    ]);

    // state is used to control the UI thread context while refs are used during sends
    const [_currentThreadId, setCurrentThreadId] = useState<
      string | undefined
    >();
    const [_triageThreadId, setTriageThreadId] = useState<string | undefined>();
    const triageThreadCallbackRef = useRef<((threadId: string) => void) | null>(
      null,
    );

    // Use refs to always have the most current values for the callback
    const currentThreadIdRef = useRef<string | undefined>();
    const triageThreadIdRef = useRef<string | undefined>();

    const toolComponents = useMemo(
      () => ({
        "update-integration": UpdateIntegrationTool,
        "query-returns": QueryReturnsTool,
        "escalate-to-human": EscalateToHumanTool,
      }),
      [],
    );

    const handleToolResult = useCallback(async (args: any) => {
      if (args.toolName === "escalate-to-human") {
        // No command palette to close in AI-only mode
      }
    }, []);

    const handleChatOpen = useCallback(() => {
      setCurrentThreadId(undefined);
      setTriageThreadId(undefined);
      currentThreadIdRef.current = undefined;
      triageThreadIdRef.current = undefined;
      // Also clear escalation triageThreadId when starting new chat
      setEscalationTriageThreadId(null);
    }, [setEscalationTriageThreadId]);

    const handleLoadThread = useCallback(
      (threadId: string, _thread: unknown) => {
        // When loading from thread history, always continue with the triage agent
        // The triage agent will handle routing to child agents as needed
        // Continue with triage agent
        setCurrentThreadId(threadId);
        setTriageThreadId(threadId);
        currentThreadIdRef.current = threadId;
        triageThreadIdRef.current = threadId;
        // Update escalation context with the triage thread ID
        setEscalationTriageThreadId(threadId);
      },
      [setEscalationTriageThreadId],
    );

    const streamMessageToAI = useCallback(
      async function* ({
        newMessages,
        signal,
      }: {
        newMessages: Message[];
        signal: AbortSignal;
      }): AsyncGenerator<AiChatStreamEvent, void, undefined> {
        // If escalation is active with human support, route messages to Intercom instead of AI
        if (
          escalation.status === "human-active" &&
          escalation.escalationId &&
          newMessages.length > 0
        ) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage && lastMessage.role === "user") {
            // Extract text content (filtering out hidden image-data and video-data metadata)
            const messageContent = extractTextFromContent(lastMessage.content);

            // Extract image and video URLs from the message content
            const attachmentUrls = Array.isArray(lastMessage.content)
              ? extractAttachmentUrls(lastMessage.content)
              : [];

            // Send message if there's text or attachments
            if (messageContent || attachmentUrls.length > 0) {
              const result = await merchantRpcClient.sendEscalationMessage({
                escalationId: escalation.escalationId,
                message: messageContent || "",
                ...(attachmentUrls.length > 0 && { attachmentUrls }),
              });

              if (!result.success) {
                yield { type: "error", error: "Failed to send message" };
                return;
              }

              // Return empty messages since the AI doesn't respond during human support
              yield { type: "complete", messages: [] };
              return;
            }
          }
        }

        // Clear closed escalation context so the user can re-escalate
        if (escalation.status === "closed") {
          clearEscalation();
        }

        const transformedMessages = transformMessagesForBackend(newMessages);

        // Use refs to get the most current values
        const currentCurrentThreadId = currentThreadIdRef.current;
        const currentTriageThreadId = triageThreadIdRef.current;

        // Page context: if set (e.g., from Returns Ask AI), always include it
        // so the triage agent has consistent context on this page.

        const stream = merchantRpcClient.streamMerchantSupportAgent(
          {
            messages: transformedMessages,
            currentThreadId: currentCurrentThreadId,
            triageThreadId: currentTriageThreadId,
            userEmail: user.email,
            merchantTimezone: Temporal.Now.timeZoneId(),
            // Always pass pageContext when available (not only for new threads)
            ...(pageContext ? { pageContext } : {}),
          },
          { signal },
        );

        let sawOmsToolCall = false;
        for await (const event of stream) {
          switch (event.type) {
            case "text-delta":
              yield { type: "text-delta", textDelta: event.textDelta };
              break;
            case "tool-call":
              if (event.toolCall.toolName === "query-oms") {
                sawOmsToolCall = true;
              }
              yield { type: "tool-call", toolCall: event.toolCall };
              break;
            case "reasoning":
              yield { type: "reasoning", text: event.text };
              break;
            case "status":
              yield { type: "status", status: event.status };
              break;
            case "complete": {
              const { thread } = event;

              // Check if this created a new triage thread
              const wasNewTriageThread =
                !currentTriageThreadId && thread.triageThreadId;

              // Update both state and refs
              setCurrentThreadId(thread.currentThreadId.toString());
              setTriageThreadId(thread.triageThreadId.toString());
              currentThreadIdRef.current = thread.currentThreadId.toString();
              triageThreadIdRef.current = thread.triageThreadId.toString();

              // If a new triage thread was created, trigger thread refresh and update escalation context
              if (wasNewTriageThread) {
                triageThreadCallbackRef.current?.(
                  thread.triageThreadId.toString(),
                );
                setEscalationTriageThreadId(thread.triageThreadId.toString());
              }

              // Refresh sidebar views after OMS queries (may have created/modified views)
              if (sawOmsToolCall) {
                void reloadViews?.({ onlyReloadIfChanged: true });
                void reloadParcels?.();
                onPageDataChanged?.();
                if (isOmsSettingsPage()) {
                  void reloadTeam?.();
                }
              }

              yield {
                type: "complete",
                messages: thread.newMessages,
                pendingTools: thread.pendingTools,
              };
              break;
            }
            case "error":
              yield { type: "error", error: event.error };
              break;
          }
        }
      },
      [
        merchantRpcClient,
        user.email,
        pageContext,
        escalation.status,
        escalation.escalationId,
        clearEscalation,
        setEscalationTriageThreadId,
        reloadViews,
        reloadParcels,
        reloadTeam,
        onPageDataChanged,
      ],
    );

    useEffect(() => {
      return () => {
        triageThreadCallbackRef.current = null;
      };
    }, []);

    // Enable file uploads for both AI chat and human support
    const chatUploader = uploader;

    const isHumanSupport = escalation.status === "human-active";

    const fileOptions = useMemo(
      () => ({
        inputAccept: isHumanSupport ? "image/*,video/*" : "image/*",
        inputMultiple: true,
        inputLabel: "Upload from computer",
      }),
      [isHumanSupport],
    );

    return (
      <AiChatProvider
        additionalToolComponents={toolComponents}
        fileUploadOptions={fileOptions}
        isHumanSupportMode={isHumanSupport}
        onToolResult={handleToolResult}
        streamMessageToAI={streamMessageToAI}
        uploader={chatUploader}
      >
        {children}
        <HelpChatGlobal
          onChatOpen={handleChatOpen}
          onLoadThread={(threadId: string, thread: unknown) =>
            handleLoadThread(threadId, thread)
          }
          onRegisterTriageThreadCallback={(
            callback: (threadId: string) => void,
          ) => {
            triageThreadCallbackRef.current = callback;
          }}
        />
      </AiChatProvider>
    );
  },
);
